import axios from "axios";
import {
  action,
  autorun,
  computed,
  observable,
  reaction,
  makeObservable,
} from "mobx";
import { fromPromise, type IPromiseBasedObservable, PENDING } from "mobx-utils";

import { type KnownLocale } from "nvent-web/i18n";
import Api from "nvent-web/services/Api";
import { type AppAuth0User } from "nvent-web/services/auth0/AppAuth0User";
import { type AppAuth0 } from "nvent-web/services/auth0/useCreateAppAuth0";
import bugsnag from "nvent-web/services/bugsnagClient";
import { type ProfileUpdate } from "nvent-web/services/resources/ProfileResource";
import { type Address } from "nvent-web/types/Address";
import { type Photo } from "nvent-web/types/Photo";
import type { Consents, Profile } from "nvent-web/types/Profile";
import { Role } from "nvent-web/types/Role";
import { never } from "nvent-web/utils/promise";

const emptyCompanyAddress: Address = {
  streetAddress: "",
  postalCode: "",
  locality: "",
  countryCode: "SE",
  region: "",
};

const initialConsents: Consents = {
  marketing: false,
  shareableProjects: true,
};

const pausedProfileUpdateKey = "installPro360.profileUpdate";

export class UserStore implements Profile {
  profilePromise = fromPromise(never());

  auth0: AppAuth0 | null = null;
  isAuthenticated = false;
  isAuthenticating = true;

  get isLoading() {
    return this.isAuthenticating || this.isProfileLoading;
  }

  get isProfileLoading() {
    return this.profilePromise.state === "pending";
  }

  get isReady() {
    return !this.isLoading && this.isAuthenticated;
  }

  get isLogoCreating(): boolean {
    return Boolean(
      this.newLogoPromise && this.newLogoPromise.state === PENDING
    );
  }

  get isAvatarCreating(): boolean {
    return Boolean(
      this.newAvatarPromise && this.newAvatarPromise.state === PENDING
    );
  }

  updatingPromise?: IPromiseBasedObservable<void>;

  auth0User: AppAuth0User | null = null;

  get isProfileComplete() {
    return Boolean(
      this.firstName &&
      this.lastName &&
      this.phoneNumber &&
      this.companyName &&
      this.companyAddress.streetAddress &&
      this.companyAddress.locality &&
      this.companyAddress.postalCode &&
      this.companyAddress.countryCode
    );
  }

  get email() {
    return this.auth0User?.email || "";
  }

  get auth0Id() {
    return this.auth0User?.auth0Id || "";
  }

  get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  id = "";
  firstName = "";
  lastName = "";
  phoneNumber = "";
  companyName = "";
  companyAddress: Address = emptyCompanyAddress;

  certifiedProNumber = "";
  locale!: KnownLocale;
  consents: Consents = { ...initialConsents };
  role = Role.installer;

  get isAdmin() {
    return this.role === Role.admin || this.role === Role.superAdmin;
  }

  get isSuperAdmin() {
    return this.role === Role.superAdmin;
  }

  logo: Photo | null = null;
  newLogoPromise?: IPromiseBasedObservable<Photo>;
  deleteLogoPromise?: IPromiseBasedObservable<Photo>;

  avatar: Photo | null = null;
  newAvatarPromise?: IPromiseBasedObservable<Photo>;
  deleteAvatarPromise?: IPromiseBasedObservable<Photo>;

  constructor(private api: Api) {
    makeObservable(this, {
      profilePromise: observable,
      auth0: observable,
      isAuthenticated: observable,
      isAuthenticating: observable,
      isLoading: computed,
      isProfileLoading: computed,
      isReady: computed,
      isLogoCreating: computed,
      isAvatarCreating: computed,
      updatingPromise: observable,
      auth0User: observable,
      isProfileComplete: computed,
      email: computed,
      auth0Id: computed,
      fullName: computed,
      id: observable,
      firstName: observable,
      lastName: observable,
      phoneNumber: observable,
      companyName: observable,
      companyAddress: observable.struct,
      certifiedProNumber: observable,
      locale: observable,
      consents: observable.struct,
      role: observable,
      isAdmin: computed,
      isSuperAdmin: computed,
      logo: observable,
      newLogoPromise: observable,
      deleteLogoPromise: observable,
      avatar: observable,
      newAvatarPromise: observable,
      deleteAvatarPromise: observable,
      isProfileUpdating: computed,
      setLocale: action.bound,
      createLogo: action.bound,
      deleteLogo: action.bound,
      createAvatar: action.bound,
      deleteAvatar: action.bound,
      setIsAuthenticated: action.bound,
      setIsAuthenticating: action.bound,
      setAuth0User: action.bound,
      setAuth0: action.bound,
      setId: action.bound,
      setFirstName: action.bound,
      setLastName: action.bound,
      setPhoneNumber: action.bound,
      setCompanyName: action.bound,
      setCertifiedProNumber: action.bound,
      setRole: action.bound,
      setConsents: action.bound,
      setLogo: action.bound,
      setAvatar: action.bound,
      setCompanyAddress: action.bound,
      setUpdatingPromise: action.bound,
    });

    this.setLocale((localStorage.getItem("app.locale") as KnownLocale) || "en");

    reaction(
      () => this.auth0Id,
      () => {
        this.profilePromise = fromPromise(this.loadProfile());
      },
      { fireImmediately: true }
    );

    autorun(() => {
      if (bugsnag) {
        bugsnag.setUser(this.auth0Id ?? undefined);
      }
    });

    autorun(() => {
      localStorage.setItem("app.locale", this.locale);
    });
  }

  setUpdatingPromise(value: typeof this.updatingPromise) {
    this.updatingPromise = value;
  }

  setCompanyAddress(value: typeof this.companyAddress) {
    this.companyAddress = value;
  }

  setId(value: typeof this.id) {
    this.id = value;
  }

  setFirstName(value: typeof this.firstName) {
    this.firstName = value;
  }

  setLastName(value: typeof this.lastName) {
    this.lastName = value;
  }

  setPhoneNumber(value: typeof this.phoneNumber) {
    this.phoneNumber = value;
  }

  setCompanyName(value: typeof this.companyName) {
    this.companyName = value;
  }

  setCertifiedProNumber(value: typeof this.certifiedProNumber) {
    this.certifiedProNumber = value;
  }

  setRole(value: typeof this.role) {
    this.role = value;
  }

  setConsents(value: typeof this.consents) {
    this.consents = value;
  }

  setLogo(value: typeof this.logo) {
    this.logo = value;
  }

  setAvatar(value: typeof this.avatar) {
    this.avatar = value;
  }

  setIsAuthenticated(value: boolean) {
    this.isAuthenticated = value;
  }

  setIsAuthenticating(value: boolean) {
    this.isAuthenticating = value;
  }

  setAuth0User(user: AppAuth0User | null) {
    this.auth0User = user;
  }

  setAuth0(value: AppAuth0 | null) {
    this.auth0 = value;
  }

  get isProfileUpdating(): boolean {
    return Boolean(
      this.updatingPromise && this.updatingPromise.state === PENDING
    );
  }

  updateProfile(changes: ProfileUpdate) {
    this.setUpdatingPromise(fromPromise(update.call(this)));
    return this.updatingPromise;

    async function update(this: UserStore) {
      if (!this.auth0) {
        throw new Error("Missing UserStore.auth0");
      }

      const idToken = await this.auth0.getIdToken();

      if (!idToken) {
        throw new Error("Missing idToken");
      }

      this.setProfile((await this.api.profile.update(changes, idToken)).data);
    }
  }

  get shouldResumeProfileUpdate() {
    return sessionStorage.getItem(pausedProfileUpdateKey) !== null;
  }

  resumeProfileUpdate() {
    const changes = this.popPausedProfileUpdate() || {};

    return this.updateProfile(changes);
  }

  async setLocale(locale: KnownLocale) {
    this.locale = locale;

    if (this.id) {
      await this.api.profile.setLocale(this.locale);
    }
  }

  async createLogo(logo: File) {
    this.newLogoPromise = fromPromise(
      this.api.profile.createLogo(logo).then(({ data }) => data)
    );

    await this.newLogoPromise;
    await this.loadProfile();

    return this.newLogoPromise;
  }

  async deleteLogo(logoId: number) {
    this.deleteLogoPromise = fromPromise(
      this.api.profile.deleteLogo(logoId).then(({ data }) => data)
    );

    await this.deleteLogoPromise;
    await this.loadProfile();

    return this.deleteLogoPromise;
  }

  async createAvatar(avatar: File) {
    this.newAvatarPromise = fromPromise(
      this.api.profile.createAvatar(avatar).then(({ data }) => data)
    );

    await this.newAvatarPromise;
    await this.loadProfile();

    return this.newAvatarPromise;
  }

  async deleteAvatar(avatarId: number) {
    this.deleteAvatarPromise = fromPromise(
      this.api.profile.deleteAvatar(avatarId).then(({ data }) => data)
    );

    await this.deleteAvatarPromise;
    await this.loadProfile();

    return this.deleteAvatarPromise;
  }

  private async loadProfile() {
    return this.setProfile(
      this.isAuthenticated ? await this.ensureProfile() : undefined
    );
  }

  private async ensureProfile(): Promise<Profile | undefined> {
    try {
      return (await this.api.profile.get()).data;
    } catch (err) {
      const isAccessDeniedError =
        axios.isAxiosError(err) && err.response?.status === 403;

      if (!isAccessDeniedError || !this.auth0) {
        throw err;
      }

      const idToken = await this.auth0.getIdToken();

      if (!idToken) {
        throw new Error("Missing idToken");
      }

      return (await this.api.profile.update({ locale: this.locale }, idToken))
        .data;
    }
  }

  private setProfile(profile: Profile | undefined) {
    this.setId(profile?.id || "");

    this.setFirstName(profile?.firstName || "");
    this.setLastName(profile?.lastName || "");
    this.setPhoneNumber(profile?.phoneNumber || "");
    this.setCompanyName(profile?.companyName || "");
    this.setCompanyAddress({
      countryCode:
        profile?.companyAddress.countryCode || emptyCompanyAddress.countryCode,
      locality: profile?.companyAddress.locality || "",
      postalCode: profile?.companyAddress.postalCode || "",
      region: profile?.companyAddress.region || "",
      streetAddress: profile?.companyAddress.streetAddress || "",
    });

    this.setCertifiedProNumber(profile?.certifiedProNumber || "");
    this.setRole(profile?.role || Role.installer);

    if (profile?.locale && profile.locale !== this.locale) {
      this.setLocale(profile.locale);
    }
    this.setConsents({
      marketing: profile?.consents.marketing ?? initialConsents.marketing,
      shareableProjects:
        profile?.consents.shareableProjects ??
        initialConsents.shareableProjects,
    });

    this.setLogo(profile?.logo || null);
    this.setAvatar(profile?.avatar || null);
  }

  private getPausedProfileUpdate(): ProfileUpdate | null {
    const changesJson = sessionStorage.getItem(pausedProfileUpdateKey);

    if (!changesJson) {
      return null;
    }

    try {
      return JSON.parse(changesJson) as ProfileUpdate;
    } catch {
      return null;
    }
  }

  private clearPausedProfileUpdate() {
    sessionStorage.removeItem(pausedProfileUpdateKey);
  }

  private popPausedProfileUpdate(): ProfileUpdate | null {
    const changes = this.getPausedProfileUpdate();
    this.clearPausedProfileUpdate();
    return changes;
  }
}
